/**
 * [TD-47.6c E1-CORRECTION + Phase 2d Session B, founder-locked redlines]
 * PaywallOverlay.
 *
 * Frosted-glass paywall over PGF Analytics for unpaid users. Context-
 * aware CTAs per the founder spec:
 *
 *   • mode="draft" (default when draftId provided)
 *       → "Unlock Draft Analytics - $4.99" + "Subscribe - $6.99/mo"
 *   • mode="season" (dashboard / in-season)
 *       → "Subscribe - $6.99/mo"
 *   • mode="auction" (commissioner gate)
 *       → "Auction Draft Pass - $34.99 / $39.99" (date-aware)
 *           + extended ($44.99) when leagueQuotaExhausted=true
 *
 * Each button opens Stripe Checkout. Success redirects to
 * /billing/success; cancel returns here with no state change.
 *
 * Copy rules (founder-locked):
 *   • Monthly = flat $6.99. No discounts, no strikethrough.
 *   • No season-pass tier. Auction $34.99 / $39.99 cutover is Aug 1, 2026.
 */
import React, { useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Auction price helper (date-aware) ────────────────────────────────
function auctionBasePrice() {
  const now = new Date();
  const cutoff = new Date("2026-08-01T00:00:00Z");
  return now < cutoff ? "$34.99" : "$39.99";
}

async function startCheckout({ product, draftId, leagueId, setBusy, setErr }) {
  setBusy(product);
  setErr(null);
  try {
    const token = localStorage.getItem("token");
    const body = { product, origin_url: window.location.origin };
    if (draftId) body.draft_id = draftId;
    if (leagueId) body.league_id = leagueId;
    const r = await fetch(`${API}/api/stripe/create-checkout-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      throw new Error(d.detail || `HTTP ${r.status}`);
    }
    const { url } = await r.json();
    window.location.href = url;
  } catch (e) {
    setErr(e.message || "Checkout failed");
    setBusy(null);
  }
}

export function PaywallOverlay({
  onUpgrade,
  draftId,
  leagueId,
  mode,                       // "draft" | "season" | "auction"
  leagueQuotaExhausted = false, // for auction mode → switches to $44.99
}) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);

  // Resolve mode (default to draft when draftId present, else season).
  const resolvedMode = mode || (draftId ? "draft" : "season");
  const ctx = { draftId, leagueId, setBusy, setErr };
  const upgrade = (product) => () => {
    if (onUpgrade) onUpgrade(product);
    startCheckout({ product, ...ctx });
  };

  return (
    <div
      data-testid="pgf-analytics-paywall"
      data-paywall-mode={resolvedMode}
      className="absolute inset-0 z-10 flex flex-col items-center justify-center"
      style={{
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        background: "rgba(10, 10, 10, 0.55)",
      }}
    >
      <div className="text-center max-w-sm px-4">
        <p className="font-display font-black text-xs uppercase tracking-widest text-[#FF6B00] mb-2">
          {resolvedMode === "auction" ? "Auction draft" : "PGF Premium"}
        </p>
        <h3 className="font-display font-black text-xl leading-tight" style={{ color: "#F5F5F5" }}>
          {resolvedMode === "auction"
            ? "Auction Draft Pass required"
            : "Unlock PGF Analytics"}
        </h3>
        <p
          className="font-body text-xs mt-2 leading-relaxed"
          style={{ color: "rgba(245,245,245,0.85)" }}
        >
          {resolvedMode === "auction"
            ? "Commissioner pays once per season. Covers up to 10 leagues."
            : "Five-axis Rank, dollar Value, weekly matchup map, gold/silver alerts, and position-specific gauges."}
        </p>

        <div className="mt-4 flex flex-col gap-2">
          {/* DRAFT MODE: $4.99 one-shot + $6.99/mo */}
          {resolvedMode === "draft" && (
            <>
              <button
                type="button"
                data-testid="paywall-cta-draft"
                disabled={!!busy || !draftId}
                onClick={upgrade("analytics_draft_access")}
                className="px-4 py-2 rounded-md bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 transition-colors font-display font-black text-sm uppercase tracking-wider text-white shadow-lg"
              >
                {busy === "analytics_draft_access" ? "Redirecting…" : "Unlock Draft Analytics - $4.99"}
              </button>
              <button
                type="button"
                data-testid="paywall-cta-monthly"
                disabled={!!busy}
                onClick={upgrade("analytics_monthly")}
                className="px-4 py-2 rounded-md border border-[#FF6B00] hover:bg-[#FF6B00]/15 disabled:opacity-50 transition-colors font-display font-black text-sm uppercase tracking-wider text-[#FF6B00]"
              >
                {busy === "analytics_monthly" ? "Redirecting…" : "Subscribe - $6.99 / mo"}
                <span className="block text-[10px] font-body font-normal opacity-80 normal-case tracking-normal mt-0.5">
                  Pregame Fantasy First Year Launch Price
                </span>
              </button>
            </>
          )}

          {/* SEASON MODE: $6.99/mo only */}
          {resolvedMode === "season" && (
            <button
              type="button"
              data-testid="paywall-cta-monthly"
              disabled={!!busy}
              onClick={upgrade("analytics_monthly")}
              className="px-4 py-2 rounded-md bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 transition-colors font-display font-black text-sm uppercase tracking-wider text-white shadow-lg"
            >
              {busy === "analytics_monthly" ? "Redirecting…" : "Subscribe - $6.99 / mo"}
              <span className="block text-[10px] font-body font-normal opacity-80 normal-case tracking-normal mt-0.5">
                Pregame Fantasy First Year Launch Price
              </span>
            </button>
          )}

          {/* AUCTION MODE: base or extended pass */}
          {resolvedMode === "auction" && (
            <>
              {!leagueQuotaExhausted ? (
                <button
                  type="button"
                  data-testid="paywall-cta-auction-base"
                  disabled={!!busy || !leagueId}
                  onClick={upgrade("auction_draft_pass")}
                  className="px-4 py-2 rounded-md bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 transition-colors font-display font-black text-sm uppercase tracking-wider text-white shadow-lg"
                >
                  {busy === "auction_draft_pass" ? "Redirecting…" : `Auction Draft Pass - ${auctionBasePrice()}`}
                  <span className="block text-[10px] font-body font-normal opacity-80 normal-case tracking-normal mt-0.5">
                    Covers up to 10 leagues this season
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  data-testid="paywall-cta-auction-extended"
                  disabled={!!busy || !leagueId}
                  onClick={upgrade("auction_draft_pass_extended")}
                  className="px-4 py-2 rounded-md bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 transition-colors font-display font-black text-sm uppercase tracking-wider text-white shadow-lg"
                >
                  {busy === "auction_draft_pass_extended" ? "Redirecting…" : "Upgrade to Extended - $44.99"}
                  <span className="block text-[10px] font-body font-normal opacity-80 normal-case tracking-normal mt-0.5">
                    Unlimited leagues - replaces base pass
                  </span>
                </button>
              )}
            </>
          )}
        </div>
        {err && (
          <p data-testid="paywall-error" className="font-body text-xs mt-3 text-red-400">
            {err}
          </p>
        )}
      </div>
    </div>
  );
}

export default PaywallOverlay;
